/**
 * Help Center content for the Support section (/support).
 *
 * Built from what students actually ask (support tickets, Sep 2023 – Oct 2026)
 * and how staff answered, checked against the app's current screens. Keep
 * answers to what a student can do themselves; anything only staff can do
 * ends with "send us a message" (`contact: true` adds the button).
 *
 * Rules for editing:
 * - Use the labels students see on screen (top bar: Dashboard, Courses,
 *   Conferences, Certificates & Forms, Your Account, Support; inside a course
 *   or conference the menu says "Certifications & Forms").
 * - No prices, year-specific links or dates that go stale.
 * - Payment plans: The Payment Option (TPO) is a separate company. Only ever
 *   say "log in to your TPO account" and "contact TPO directly" — never TPO
 *   instructions, policies or contact details (Derek 2026-10-16).
 * - `a` is trusted HTML rendered with v-html; in-app links use the hash form
 *   (#/reports/receipts). Main destinations go in `actions` as buttons.
 * - `certification` (1 clock hours, 2 WIAA, 3 WSCA) orders a topic or article
 *   after the student's own programs; nothing is hidden.
 */
import {LEGAL} from './legal.js';
import {TPO_URL} from '../utils/paymentPlans.js';

const EMAIL = LEGAL.supportEmail;
const mail = `<a href="mailto:${EMAIL}">${EMAIL}</a>`;
const FINALFORMS_EMAIL = '<a href="mailto:support@finalforms.com">support@finalforms.com</a>';

export const HELP_TOPICS = [
    {key: 'account', title: 'Account & Login', icon: 'fa-user-lock', blurb: 'Logging in, login codes, passwords, duplicate accounts and your personal details.'},
    {key: 'wiaa', title: 'WIAA Coaches & FinalForms', icon: 'fa-whistle', certification: 2, blurb: 'Coach clinics, Coaches School, WIAA Membership IDs and syncing with FinalForms.'},
    {key: 'enrolling', title: 'Finding & Enrolling in Courses', icon: 'fa-browsers', blurb: 'The course catalog, access codes, enrolling, bundles and enrollment limits.'},
    {key: 'taking', title: 'Taking a Course', icon: 'fa-chalkboard-teacher', blurb: 'Lessons, time tracking, notes and files, and fixing video or page problems.'},
    {key: 'studyplan', title: 'Study Plans, Logging Time & Reflections', icon: 'fa-pen-to-square', blurb: 'Self-directed courses: your study plan, the study log, milestones, reflections and STEM.'},
    {key: 'completing', title: 'Finishing a Course & Applying for Credit', icon: 'fa-flag-checkered', blurb: 'Completing coursework, certification applications, approval and application fees.'},
    {key: 'clockhours', title: 'Clock Hour Forms & OSPI', icon: 'fa-file-certificate', certification: 1, blurb: 'Your FORM SPI 1125, submitting hours to OSPI or your district, and renewal.'},
    {key: 'expiration', title: 'Expiration, Reactivation & Changing Courses', icon: 'fa-hourglass-half', blurb: 'When courses expire, reactivating, the grace period and reallocating hours.'},
    {key: 'conferences', title: 'Conferences & Registrations', icon: 'fa-presentation', blurb: 'Registering, access codes, attendance, purchase orders and conference credit.'},
    {key: 'payments', title: 'Payments, Receipts & Refunds', icon: 'fa-receipt', blurb: 'Receipts, invoices, sales tax, refunds and payment plans.'},
    {key: 'notifications', title: 'E-Mails & Text Messages', icon: 'fa-bell', blurb: 'What we send, turning texts on or off, and unsubscribing.'},
    {key: 'support', title: 'Getting Help From Our Team', icon: 'fa-messages-question', blurb: 'What our team can do for you and how to reach us.'},
];

export const HELP_ARTICLES = [
    // ── Account & Login ────────────────────────────────────────────────
    {
        id: 'login-methods',
        topic: 'account',
        q: 'How do I log in?',
        keywords: 'sign in login password code email phone text access cant log in',
        a: `<p>Go to <strong>app.my247ed.com</strong> and enter your <strong>e-mail address or phone number</strong>, then press <strong>Login</strong>. You can then choose how to sign in:</p>
<ul>
<li><strong>Login with your Password</strong>: this works even if you no longer have access to that e-mail inbox.</li>
<li><strong>Login with your E-Mail</strong>: we e-mail you a 5-digit access code.</li>
<li><strong>Login with your Phone</strong>: we text you a 5-digit access code (the phone must be able to receive texts).</li>
</ul>
<p>Access codes are good for 30 minutes and can only be used once. Your secondary e-mail address can be used to log in too. Your work phone can't.</p>`,
    },
    {
        id: 'login-no-account-found',
        topic: 'account',
        q: "It says no account was found, or I don't know which e-mail I used",
        keywords: 'no account found email does not exist old school email lost access forgot email which email',
        a: `<p>Try each e-mail address and phone number you might have used, including old school or district addresses. Your account may be under an older one.</p>
<p>If you've lost access to the old e-mail, log in with your <strong>phone number</strong> instead, or use your password. Once you're in, update your details under <strong>Your Account › Login Credentials</strong>.</p>
<p>The <strong>Login Assistance</strong> page walks through each option. If none of them work, send us every e-mail and phone number you may have used and we'll find your account.</p>`,
        actions: [{label: 'Login Assistance', href: '#/assistance', icon: 'fa-circle-question'}],
        contact: true,
    },
    {
        id: 'password-reset',
        topic: 'account',
        q: 'How do I reset or change my password?',
        keywords: 'forgot password reset change password link expired',
        a: `<p><strong>Forgot it?</strong> On the <strong>Login Assistance</strong> page, enter your e-mail address and press <strong>Reset Password</strong>. The reset link is good for 30 minutes. Check your spam or junk folder if it doesn't arrive.</p>
<p><strong>Want to change it?</strong> Log in any way you can (an e-mail or text code works), then go to <strong>Your Account › Login Credentials</strong> and enter a new password.</p>`,
        actions: [{label: 'Login Credentials', to: {name: 'account', params: {section: 'login'}}, icon: 'fa-key'}],
    },
    {
        id: 'change-email',
        topic: 'account',
        q: 'How do I change my e-mail address or add a second one?',
        keywords: 'change email update email secondary alternate email primary swap',
        a: `<p>Go to <strong>Your Account › Login Credentials</strong>. You can change your <strong>Primary E-Mail Address</strong> and add a <strong>Secondary E-Mail Address</strong>. Both can be used to log in, and the secondary one is our backup if mail to your primary fails.</p>
<p>If we say an address is already in use, you probably have a second account with us. Send us a message and we'll combine them.</p>`,
        actions: [{label: 'Login Credentials', to: {name: 'account', params: {section: 'login'}}, icon: 'fa-key'}],
    },
    {
        id: 'duplicate-accounts',
        topic: 'account',
        q: 'I think I have two accounts. Can you merge them?',
        keywords: 'duplicate account two accounts merge combine accounts second account created new account',
        a: `<p>Yes, our team can merge them. Send us a message listing every e-mail address and phone number you may have used so we can find all of your records.</p>
<p>Please don't create a new account to get around a login problem. You should only ever have one account. Duplicates split your history, and they're the most common reason WIAA certifications don't sync to FinalForms.</p>`,
        contact: true,
    },
    {
        id: 'name-change',
        topic: 'account',
        q: 'How do I change my name?',
        keywords: 'name change married maiden last name misspelled name wrong name legal name',
        a: `<p>For record keeping, name changes are made by our team. E-mail ${mail} with a photo of the front of your updated identification, such as a driver's license, showing the new name.</p>
<p>Your name prints on your certificates and clock-hour forms, so it's best to update it before you download them.</p>`,
    },
    {
        id: 'update-profile',
        topic: 'account',
        q: 'How do I update my address, phone, school or district?',
        keywords: 'address phone number moved school district update profile personal information',
        a: `<p>Go to <strong>Your Account › Account Settings</strong>, make your changes and press <strong>Save Changes</strong>.</p>
<p>If you change your cell phone number, text messages are turned off for the new number until you opt in again under <strong>Your Account › Notifications</strong>.</p>`,
        actions: [{label: 'Account Settings', to: {path: '/account'}, icon: 'fa-user-circle'}],
    },
    {
        id: 'shared-account',
        topic: 'account',
        q: 'Can I take over or share an account (for example, a previous AD\'s)?',
        keywords: 'shared account another person previous athletic director new ad transfer account someone else sign up for someone',
        a: `<p>No. Each account belongs to one person, and the certifications and clock hours on it are that person's records. Please create your own account, and don't sign up or register on someone else's behalf.</p>`,
        actions: [{label: 'Sign Up', href: '#/signup', icon: 'fa-user-plus'}],
    },
    {
        id: 'delete-account',
        topic: 'account',
        q: 'Can I delete or close my account?',
        keywords: 'delete account close account disable deactivate remove my data',
        a: `<p>We can <strong>disable</strong> your account so it can no longer be used. Send us a message asking us to do that.</p>
<p>Accounts aren't deleted. Your account holds official records (certificates, clock-hour forms and study logs), so a disabled account is still kept for record keeping, and it's there if you need those records later for renewal or an audit.</p>`,
        contact: true,
    },

    // ── WIAA Coaches & FinalForms ──────────────────────────────────────
    {
        id: 'wiaa-sync',
        topic: 'wiaa',
        certification: 2,
        q: "My WIAA certification isn't showing in FinalForms",
        keywords: 'finalforms final forms sync not syncing not showing certification missing wiaa clinic finished',
        a: `<p>FinalForms pulls your completed WIAA certifications from us. When one is missing, it's almost always one of these:</p>
<ol>
<li><strong>You haven't applied for the certification yet.</strong> Finishing the clinic isn't enough. At the end of every clinic or conference, open <strong>Certifications &amp; Forms</strong> inside it and complete the application. Check <strong>Certificates &amp; Forms › Completed Certificates</strong>: if the certification isn't listed there, or shows an older date, it isn't finished.</li>
<li><strong>Your WIAA Membership ID isn't set.</strong> FinalForms matches on your WIAA ID only, not your name or e-mail. Go to <strong>Your Account › Certifications &amp; Organizations</strong> and check the WIAA card. If it's blank, use <strong>Lookup WIAA Membership ID</strong>, enter it and press <strong>Save Changes</strong>.</li>
<li><strong>You have a duplicate account.</strong> If we warn that your WIAA ID is in use by another account, send us a message with all e-mails and phone numbers you may have used and we'll merge them.</li>
<li><strong>You have more than one WIAA ID</strong> (common after changing districts). See "I have more than one WIAA ID".</li>
</ol>
<p>Once everything above is in place, press <strong>Sync Certifications with my247ed</strong> in FinalForms. If it still doesn't appear, FinalForms support (${FINALFORMS_EMAIL}) or your Athletic Director can help on their side.</p>`,
        actions: [
            {label: 'Completed Certificates', to: {name: 'reports', params: {view: 'completed'}}, icon: 'fa-file-certificate'},
            {label: 'Check My WIAA ID', to: {name: 'account', params: {section: 'certs'}}, icon: 'fa-id-card'},
            {label: 'Coach Walk-Through', href: '#/wiaa-help', icon: 'fa-route'},
        ],
    },
    {
        id: 'wiaa-sync-by-email',
        topic: 'wiaa',
        certification: 2,
        q: 'My name and e-mail match in both systems. Why won\'t it sync?',
        keywords: 'same email same name sync finalforms match',
        a: `<p>FinalForms doesn't match on name, e-mail or address. It only uses the <strong>WIAA Membership ID</strong> saved in your my247ed account, so changing your e-mail or name won't fix a sync problem.</p>
<p>Check the ID under <strong>Your Account › Certifications &amp; Organizations</strong>, and make sure it's the same ID FinalForms has for you.</p>`,
        actions: [{label: 'Check My WIAA ID', to: {name: 'account', params: {section: 'certs'}}, icon: 'fa-id-card'}],
    },
    {
        id: 'wiaa-multiple-ids',
        topic: 'wiaa',
        certification: 2,
        q: 'I have more than one WIAA ID',
        keywords: 'multiple wiaa ids two ids changed districts coach multiple districts consolidate membership id',
        a: `<p>WIAA IDs are issued by WIAA and FinalForms, so only FinalForms can combine them. Contact FinalForms support at ${FINALFORMS_EMAIL} and tell them which WIAA Membership ID is saved in your my247ed account (you'll find it under <strong>Your Account › Certifications &amp; Organizations</strong>). They'll consolidate your records.</p>
<p>If the ID in your my247ed account is wrong, you can correct it on that same screen, unless it's locked. A locked ID was confirmed by FinalForms, and our team can change it for you.</p>`,
        actions: [{label: 'Check My WIAA ID', to: {name: 'account', params: {section: 'certs'}}, icon: 'fa-id-card'}],
    },
    {
        id: 'wiaa-print-certificate',
        topic: 'wiaa',
        certification: 2,
        q: 'Can I print my coaching certificate for my AD?',
        keywords: 'print certificate coaching download proof athletic director',
        a: `<p>WIAA coaching certifications are tracked digitally and sync to FinalForms, which is where your district checks them. There's usually no certificate to print.</p>
<p>Your record of every awarded certification is under <strong>Certificates &amp; Forms › Completed Certificates</strong>, along with a button to view your certification status at FinalForms.</p>`,
        actions: [{label: 'Completed Certificates', to: {name: 'reports', params: {view: 'completed'}}, icon: 'fa-file-certificate'}],
    },
    {
        id: 'wiaa-where-clinics',
        topic: 'wiaa',
        certification: 2,
        q: 'Where do I find rules clinics, hands-on clinics and Coaches School?',
        keywords: 'rules clinic hands on clinic coaches school coaches education where find register sport clinic',
        a: `<ul>
<li><strong>Rules clinics</strong> (General Rules and sport-specific) are online courses in the <strong>Online Course Catalog</strong>.</li>
<li><strong>Hands-on clinics</strong> (such as tackling, stunt or pole vault) and <strong>Coaches School</strong> are conferences. Register in the <strong>Conference Center</strong>, then find them under <strong>Conferences › Conference Records</strong>.</li>
<li>Coaches School sessions are watched under <strong>On-Demand Content</strong> inside the conference.</li>
</ul>
<p>Nothing is added to your account automatically. Even if your district signed you up or you attended in person, you need to register yourself.</p>`,
        actions: [
            {label: 'Course Catalog', to: {name: 'course-catalog'}, icon: 'fa-browsers'},
            {label: 'Conference Center', to: {name: 'conference-center'}, icon: 'fa-presentation'},
        ],
    },
    {
        id: 'wiaa-not-listed',
        topic: 'wiaa',
        certification: 2,
        q: "The clinic I need isn't listed yet",
        keywords: 'clinic not listed not available yet next season when open release coaches school closed registration closed full',
        a: `<p>WIAA decides when each clinic is released and when registration opens and closes. New season content usually appears before that season starts. If a hands-on clinic isn't listed, it hasn't been scheduled yet or it's full.</p>
<p>For clinic schedules, ask your Athletic Director or WIAA.</p>`,
    },
    {
        id: 'wiaa-cant-begin',
        topic: 'wiaa',
        certification: 2,
        q: "A WIAA course says it has unmet requirements, or Begin won't work",
        keywords: 'unmet requirements begin button locked prerequisite coaches update form general rules',
        a: `<p>Some WIAA courses require another step first, such as the season's Coaches Update form in the General Rules clinic. Open the course's <strong>Course Information</strong> page to see what's needed, finish that step, and the course will unlock.</p>
<p>On questionnaires, answer anything that doesn't fit you with "Does not apply" or "Prefer not to say".</p>`,
    },
    {
        id: 'wiaa-minutes-short',
        topic: 'wiaa',
        certification: 2,
        q: 'Coaches School says I\'m short on minutes',
        keywords: 'short minutes not enough time on demand video track legal medical minutes required',
        a: `<p>On-demand time is recorded in <strong>full minutes</strong> of actual viewing: 59 seconds counts as 0, and faster playback doesn't add time. Your progress shows under each video.</p>
<p>If you're a few minutes short, rewatch part of a video or watch another session in the same track until you reach the required minutes, then apply.</p>`,
    },
    {
        id: 'wiaa-purchase-order',
        topic: 'wiaa',
        certification: 2,
        q: "I paid by purchase order. Why can't I access my clinic or apply?",
        keywords: 'purchase order po pending locked district pay wiaa po content locked',
        a: `<p>WIAA processes its own purchase orders. Until WIAA marks your P.O. as received, the registration is unpaid: the content stays locked and the certification can't be awarded. Use <strong>Contact Organizer</strong> in the registration, or check with your Athletic Director.</p>
<p><strong>Picked purchase order by mistake, or now paying by card?</strong> Send us a message and we'll clear the P.O. registration so you can register again with a card.</p>`,
        contact: true,
    },
    {
        id: 'wiaa-expired-content',
        topic: 'wiaa',
        certification: 2,
        q: 'I finished a clinic last season but never applied. Can I still get it?',
        keywords: 'last season expired never applied old clinic previous year',
        a: `<p>Once WIAA retires a season's content, its certification application can no longer be completed, so you'll need to take the current season's version. To avoid this, apply as soon as you finish each clinic.</p>`,
    },
    {
        id: 'wiaa-clock-hours',
        topic: 'wiaa',
        certification: 2,
        q: 'Do I need clock hours for my coaching clinic?',
        keywords: 'clock hours coaching clinic foundations of coaching optional fee pay ospi asking to pay certificate',
        a: `<p>No. Coaching certifications don't require clock hours. Clock hours are optional and only matter to certificated educators earning Washington professional development hours. Many short clinics don't qualify for clock hours at all.</p>
<p>If a clinic or course asks you to pay after you've finished, it's almost always the optional clock-hour application. You can skip it, and there's no fee to sync your WIAA certification to FinalForms. Foundations of Coaching courses are optional and don't award a WIAA certification.</p>`,
    },
    {
        id: 'wiaa-finalforms-other',
        topic: 'wiaa',
        certification: 2,
        q: 'I need help with my FinalForms account or an NFHS course',
        keywords: 'finalforms login finalforms password add athlete child nfhs course',
        a: `<p>Those are separate systems, so we can't access them. For FinalForms logins or athlete forms, contact FinalForms support at ${FINALFORMS_EMAIL}. For NFHS courses, contact NFHS.</p>`,
    },

    // ── Finding & Enrolling ────────────────────────────────────────────
    {
        id: 'find-courses',
        topic: 'enrolling',
        q: 'How do I find courses?',
        keywords: 'find courses catalog browse search educator courses legacy where courses',
        a: `<p>Every course is in the <strong>Online Course Catalog</strong> (Courses › Online Course Catalog). Pick your certification, then a category, then a course to see its description, goals, outline and enrollment options.</p>
<p>The old "Legacy" site has been retired. Everything now lives in the catalog.</p>`,
        actions: [{label: 'Course Catalog', to: {name: 'course-catalog'}, icon: 'fa-browsers'}],
    },
    {
        id: 'access-code',
        topic: 'enrolling',
        q: 'I have a course or conference access code. Where do I enter it?',
        keywords: 'access code training code course code conference code enter code invalid code',
        a: `<ul>
<li><strong>Course code:</strong> enter it in the <strong>Access a Training Course</strong> box in the Online Course Catalog and press <strong>Open Course</strong>.</li>
<li><strong>Conference code:</strong> enter it in the <strong>Access/Discount Code</strong> field when you register for the conference.</li>
</ul>
<p>Both can also be entered under <strong>Quick Access</strong> on the login page. Access codes aren't a search box: if a code is rejected, check it with whoever gave it to you.</p>`,
        actions: [{label: 'Course Catalog', to: {name: 'course-catalog'}, icon: 'fa-browsers'}],
    },
    {
        id: 'how-enroll',
        topic: 'enrolling',
        q: 'How do I enroll in a course?',
        keywords: 'enroll how to enroll sign up course purchase buy course',
        a: `<ol>
<li>Open the course in the <strong>Online Course Catalog</strong> and press <strong>Enroll</strong>.</li>
<li>Choose an <strong>Enrollment Option</strong> (the number of hours).</li>
<li>Some courses ask for a <strong>Course Name</strong> so you can tell repeat enrollments apart.</li>
<li>Pay by card (if there's a fee), read and sign the terms, and press <strong>Complete Enrollment</strong>.</li>
</ol>
<p>The course then appears under <strong>Courses › Current Enrollments</strong>, where you can start any time before it expires.</p>`,
        actions: [{label: 'Course Catalog', to: {name: 'course-catalog'}, icon: 'fa-browsers'}],
    },
    {
        id: 'enrollment-limit',
        topic: 'enrolling',
        q: 'Why does it say the enrollment limit for a course has been reached?',
        keywords: 'enrollment limit reached repeat same course again how many times book review',
        a: `<p>Some courses can only be taken a set number of times within a period of years (for example, 3 times in 2 years). Every enrollment counts toward the limit, including completed and expired ones. The course page shows when you can enroll again.</p>
<p>Courses that allow repeats need a different <strong>Course Name</strong> for each enrollment.</p>`,
    },
    {
        id: 'which-course',
        topic: 'enrolling',
        q: 'Which course should I take?',
        keywords: 'which course recommend best course choose course goals',
        a: `<p>Choose a course that fits what you're working on: your classroom, a new curriculum, technology, a book you want to study. Self-directed courses let you build your own goals and objectives around it. Use the catalog's categories to narrow things down.</p>
<p>If you need a lot of hours, <strong>Work With a Representative</strong> to plan them, or send us a message describing your goals.</p>`,
        actions: [{label: 'Course Catalog', to: {name: 'course-catalog'}, icon: 'fa-browsers'}],
    },
    {
        id: 'bundles',
        topic: 'enrolling',
        certification: 1,
        q: 'What is a bundle?',
        keywords: 'bundle package build a bundle many hours discount salary lane representative',
        a: `<p>A bundle is a block of clock hours you buy at a lower rate and then spend across courses in the catalog. It's a good fit when you need a lot of hours, for example to move up your district's salary schedule. Use <strong>Build a Bundle</strong> to size one; it explains pricing, payment and expiration as you go.</p>
<p>Prefer help? Choose <strong>Work With a Representative</strong> and someone will plan your hours and enroll you.</p>`,
        actions: [
            {label: 'Build a Bundle', to: {name: 'package-builder'}, icon: 'fa-layer-group'},
            {label: 'Work With a Representative', to: {name: 'package-builder', query: {phase: 'lead'}}, icon: 'fa-phone'},
        ],
    },
    {
        id: 'cart-checkout',
        topic: 'enrolling',
        q: 'A representative set up my courses. How do I finish enrolling?',
        keywords: 'representative cart checkout complete enrollment cart rep set up',
        a: `<p>When a representative prepares courses for you, your Dashboard shows <strong>Action Required: Complete Your Cart Enrollment</strong>. Open it, review the courses and terms, and complete checkout to add them to your account.</p>`,
        actions: [{label: 'Dashboard', to: {name: 'dashboard'}, icon: 'fa-home'}],
    },

    // ── Taking a Course ────────────────────────────────────────────────
    {
        id: 'start-course',
        topic: 'taking',
        q: 'How do I start or continue a course?',
        keywords: 'start course begin course continue open course where my course',
        a: `<p>Go to <strong>Courses › Current Enrollments</strong> and press the main button on the course: <strong>Open Course</strong>, <strong>Continue</strong>, or the next step it names (such as <strong>Set Up Study Plan</strong>).</p>
<p>Inside the course, the menu on the left has the Course Outline, your notes and files, the study log, reflections and <strong>Certifications &amp; Forms</strong>.</p>`,
        actions: [{label: 'Current Enrollments', to: {path: '/enrollments'}, icon: 'fa-circle-play'}],
    },
    {
        id: 'lessons-in-order',
        topic: 'taking',
        q: "Why can't I skip ahead to the next lesson?",
        keywords: 'next lesson locked skip ahead complete lesson button disabled order',
        a: `<p>Lessons go in order. At the bottom of each lesson, press <strong>Complete Lesson</strong> (or <strong>Complete Section</strong>) to unlock the next one. Some lessons need a video watched or a minimum amount of time spent before the button turns on.</p>
<p>In self-directed courses, the outline also stays locked until your study plan is approved, and a milestone reflection can pause the course until it's accepted.</p>`,
    },
    {
        id: 'idle-timer',
        topic: 'taking',
        q: '"Are you still there?" Why did my time tracking pause?',
        keywords: 'are you still there idle inactivity time tracking paused timer stopped kicked out',
        a: `<p>Course time is only tracked while you're active. After a few minutes with no activity, we pause tracking and ask whether you're still there; if you don't answer, you're taken back to the course overview. A playing video counts as activity.</p>
<p>Click to continue and tracking resumes. Time while paused isn't counted.</p>`,
    },
    {
        id: 'tech-problems',
        topic: 'taking',
        q: "Videos won't play, there's no sound, or a page won't load",
        keywords: 'video wont play no sound audio blank page not loading error browser broken slides',
        a: `<p>These problems are usually caused by the browser or network. Try these in order:</p>
<ol>
<li>Refresh the page. If the app asks you to update, accept.</li>
<li>Try a private/incognito window, or a different browser (Chrome, Edge, Safari or Firefox).</li>
<li>Check that the site isn't muted in your browser and that your device volume is up.</li>
<li>Try another network: school and district networks sometimes block video.</li>
<li>If you see a small-screen warning on a computer, reset your browser zoom (Ctrl + 0, or Cmd + 0 on a Mac).</li>
</ol>
<p>Some WIAA clinics are slides with no audio. If none of this helps, send us a message saying which course, which lesson, and the device and browser you're using.</p>`,
        contact: true,
    },
    {
        id: 'phone-tablet',
        topic: 'taking',
        q: 'Can I take courses on my phone?',
        keywords: 'phone mobile tablet ipad device',
        a: `<p>For the best experience, use a computer or tablet. Phones work for checking your account, but course lessons, study plans and reflections are much easier on a bigger screen.</p>`,
    },
    {
        id: 'notes-files',
        topic: 'taking',
        q: 'Where do I keep notes and upload files?',
        keywords: 'notes upload file attach document files and uploads',
        a: `<p>Inside a course, open <strong>Online Course Notes</strong> to write notes; they save automatically and stay with that enrollment. Use <strong>Files &amp; Uploads</strong> to upload documents (PDF, Word, Excel, PowerPoint, text and images, up to 25 MB each).</p>
<p>Forgot to attach a file to something you already submitted? E-mail it to ${mail} with the course name and we'll attach it.</p>`,
    },

    // ── Study Plans, Logging Time & Reflections ────────────────────────
    {
        id: 'study-plan',
        topic: 'studyplan',
        q: 'What is the study plan and how long does approval take?',
        keywords: 'study plan setup course study plan approval pending goals objectives how long',
        a: `<p>Self-directed courses start with a <strong>Course Study Plan</strong>: your background, your goals and objectives, how your hours split across them, and how you'll evaluate your work. Drafts save automatically.</p>
<p>After you press <strong>Submit for Approval</strong>, a reviewer checks it, typically within 24–48 hours. <strong>Don't start course work until your plan is approved</strong>: time only counts from that point. Once approved, the plan is locked in.</p>
<p>If the reviewer asks for changes, you'll see <strong>Changes Requested</strong> with their feedback. Update the plan and resubmit.</p>`,
        actions: [{label: 'Current Enrollments', to: {path: '/enrollments'}, icon: 'fa-circle-play'}],
    },
    {
        id: 'change-goals',
        topic: 'studyplan',
        q: 'Can I change my goals after my plan is approved?',
        keywords: 'change goals change plan different book adapt objectives reset study plan',
        a: `<p>An approved plan is locked in, but your goals are a map, not a checklist. You can adapt your activities and read additional material related to your goals without changing the plan.</p>
<p>If you want to work toward different goals, you can move your unused hours into a new course with its own study plan (see "Can I move hours to a different course?").</p>`,
    },
    {
        id: 'log-time',
        topic: 'studyplan',
        q: 'How do I log my study time, and what are the rules?',
        keywords: 'log time study log log activity offline time entry rules 4 hours overlap future',
        a: `<p>Open the course's <strong>Study Log</strong> and press <strong>Log Activity</strong>. Enter the date, start and end time, and the objective you worked on. Each entry has to follow these rules:</p>
<ul>
<li>It can be up to 4 hours long. Log longer sessions as separate entries.</li>
<li>It can't be in the future, or before your course start date (when your study plan was approved).</li>
<li>It can't overlap time logged in another course or conference.</li>
<li>It can't fall during your contracted (paid) work time. Prep time and time before or after school are fine; your district's policy applies if it's stricter.</li>
</ul>
<p>You can remove entries you added yourself. Lesson time recorded automatically can't be removed.</p>`,
    },
    {
        id: 'retroactive-hours',
        topic: 'studyplan',
        q: 'Can I log hours I did before I enrolled or before my plan was approved?',
        keywords: 'retroactive hours before enrolled before approved past work paper log excel earlier dates backdate',
        a: `<p>No. Washington clock hours can't be awarded for work done before the course started, so the study log won't accept dates before your course start date (when your study plan was approved).</p>
<p>Always enroll, and get your study plan approved, <em>before</em> you begin the work.</p>`,
    },
    {
        id: 'wrong-course-logged',
        topic: 'studyplan',
        q: 'I logged time to the wrong course',
        keywords: 'wrong course logged mistake move time entry remove entry',
        a: `<p>Remove the entry from that course's <strong>Study Log</strong>, then add it to the correct course. If the time is already part of a submitted reflection or application, send us a message instead.</p>`,
        contact: true,
    },
    {
        id: 'milestones',
        topic: 'studyplan',
        q: "Why is my course paused at a milestone?",
        keywords: 'milestone checkpoint paused locked cant log time reflection due 10 hours',
        a: `<p>Self-directed courses have a <strong>milestone every 10 logged hours</strong> (plus one at the end). When you reach one, time logging pauses until your reflection for that block is <strong>submitted and accepted</strong>. Submitting alone doesn't unlock the course.</p>
<p>Go to <strong>Milestones &amp; Reflections</strong> in the course to write it. While it's in review, you can work on your other courses.</p>`,
    },
    {
        id: 'reflections-good',
        topic: 'studyplan',
        q: 'What makes a reflection acceptable?',
        keywords: 'reflection rejected acceptable rubric how long what to write requirements sent back',
        a: `<p>Each reflection covers the work since your last milestone (up to 10 hours). Reviewers look for four things:</p>
<ul>
<li><strong>On topic</strong>: it connects to your goals and objectives.</li>
<li><strong>Specific</strong>: what you did, the resources you used, and what came of it.</li>
<li><strong>New</strong>: not repeated from earlier reflections or other courses, and not copy-pasted.</li>
<li><strong>Substantive</strong>: enough to reflect the hours. A solid paragraph or two is typical.</li>
</ul>
<p>Copy-pasted or repetitive reflections are sent back. Reflections are kept as your record in case of an audit.</p>`,
    },
    {
        id: 'reflections-review',
        topic: 'studyplan',
        q: 'What happens after I submit a reflection?',
        keywords: 'who reviews reflection ai review how long pending review reviewer',
        a: `<p>Every reflection is screened against the rubric. Anything that needs a closer look goes to a reviewer, who sends specific suggestions if changes are needed. You'll get an e-mail when it's reviewed.</p>
<p>If changes are requested, open <strong>Milestones &amp; Reflections</strong> in the course and submit an <strong>Updated Reflection</strong>.</p>`,
    },
    {
        id: 'reflection-mistake',
        topic: 'studyplan',
        q: 'I submitted the wrong reflection, or resubmitted without changes',
        keywords: 'submitted wrong reflection resubmitted by mistake send back kick back edit submitted',
        a: `<p>Send us a message with the course name and we'll send the reflection back to you so you can edit it and submit again.</p>
<p>Tip: write long reflections in a separate document and paste them in. If your session times out, text you haven't submitted can't be recovered.</p>`,
        contact: true,
    },
    {
        id: 'stem',
        topic: 'studyplan',
        certification: 1,
        q: 'How do I earn STEM clock hours?',
        keywords: 'stem hours science technology engineering math stem requirement renewal stem reflection',
        a: `<p>Most self-directed courses can count toward STEM. When you build your study plan, choose a STEM objective and check <strong>Count my qualifying hours as STEM clock hours</strong>. Time you log against STEM objectives becomes your STEM hours.</p>
<p>The activity has to genuinely integrate <strong>at least 2 of the 4 STEM elements</strong>. In every reflection that includes STEM time, answer all three questions, or the time won't count as STEM:</p>
<ol>
<li>How does this STEM activity impact the STEM student experience?</li>
<li>What examples and resources were used with students or other educators?</li>
<li>How does the activity provide examples or resources about STEM-related career choices?</li>
</ol>
<p>Reviewers may ask for supporting materials. If they still aren't provided after two requests, the hours may be awarded as regular clock hours rather than STEM.</p>`,
    },
    {
        id: 'study-activity',
        topic: 'studyplan',
        q: 'I got an "unusual activity" alert on Study Activity',
        keywords: 'unusual activity audit flagged study activity calendar requires attention implausible',
        a: `<p><strong>Study Activity</strong> (Certificates &amp; Forms › Study Activity) shows all your logged time on one calendar. Days with an unusually large amount of time, overlapping entries, or time during your contracted schedule are flagged.</p>
<p>The alert asks you to double-check your log: look for a timer left running or a duplicate entry, and correct anything that's wrong. Your study log is the record you'd provide in an audit, and time that can't be accounted for may be removed.</p>`,
        actions: [{label: 'Study Activity', to: {name: 'study-activity'}, icon: 'fa-calendar-days'}],
    },
    {
        id: 'work-schedule',
        topic: 'studyplan',
        q: 'What is the Contracted Work Schedule?',
        keywords: 'work schedule contracted schedule contract hours school day',
        a: `<p>Under <strong>Your Account › Work Schedule</strong> you declare your contracted (paid) work hours. Study time can't be logged during those hours, and anything logged inside them is flagged for review. Changes apply going forward.</p>`,
        actions: [{label: 'Work Schedule', to: {name: 'account', params: {section: 'schedule'}}, icon: 'fa-calendar-clock'}],
    },

    // ── Finishing & Applying ───────────────────────────────────────────
    {
        id: 'finish-course',
        topic: 'completing',
        q: "I've finished my course. What's next?",
        keywords: 'finished course what next complete course get credit get hours done',
        a: `<p>Open the course and go to <strong>Certifications &amp; Forms</strong> in its menu, then apply for each certification or form you need. The course is complete once every lesson is done and every <em>required</em> application is finished.</p>
<p>If only optional applications are left, we'll ask you to confirm with <strong>Complete My Course</strong> or <strong>Keep My Course Open</strong>. Completing locks your study log and can't be undone, so apply for everything you want first.</p>
<p>To see everything you can apply for across your courses and conferences, check <strong>Certificates &amp; Forms › Available Applications</strong>.</p>`,
        actions: [{label: 'Available Applications', to: {name: 'reports', params: {view: 'available'}}, icon: 'fa-file-circle-plus'}],
    },
    {
        id: 'apply-steps',
        topic: 'completing',
        q: 'How do I apply for my clock hours or certification?',
        keywords: 'apply application clock hours certification how to apply apply button requirements',
        a: `<ol>
<li>In the course (or conference), open <strong>Certifications &amp; Forms</strong> and choose <strong>View Details</strong> on the application.</li>
<li>Confirm that your study log is accurate. Later changes to the log won't update the application, and <strong>you can't reapply once you've applied</strong>.</li>
<li>Make sure the other requirements are met: all lessons finished, no reflection waiting for approval, and any minimum time reached.</li>
<li>Press <strong>Apply</strong> and follow the steps.</li>
</ol>
<p>Hours are calculated from your logged time, up to the hours you enrolled in. The final number may differ slightly due to rounding.</p>`,
        actions: [{label: 'Available Applications', to: {name: 'reports', params: {view: 'available'}}, icon: 'fa-file-circle-plus'}],
    },
    {
        id: 'apply-not-ready',
        topic: 'completing',
        q: "Why can't I apply yet?",
        keywords: 'cant apply not ready more time needed finish coursework requirements apply button greyed',
        a: `<p><strong>Certificates &amp; Forms › Available Applications</strong> shows each application's status and what's missing, for example <em>More Time Needed</em>, <em>Finish Coursework</em>, <em>Reflection In Review</em>, <em>Study Plan Needed</em> or <em>Enrollment Expired</em>.</p>
<p>If it says <em>Certification Off</em>, turn that certification on under <strong>Your Account › Certifications &amp; Organizations</strong>.</p>`,
        actions: [{label: 'Available Applications', to: {name: 'reports', params: {view: 'available'}}, icon: 'fa-file-circle-plus'}],
    },
    {
        id: 'approval-time',
        topic: 'completing',
        q: 'How long does approval take?',
        keywords: 'how long approval in review waiting pending application review time deadline salary deadline',
        a: `<p>Applications that need review are usually approved within 48 hours, and you'll get an e-mail when yours is. You can follow its status under <strong>Certificates &amp; Forms › In Progress Applications</strong>.</p>
<p>Facing a district or salary deadline? Send us a message with the date and we'll do our best to prioritize it. Some partner conference applications wait on the partner organization's review, which can take longer.</p>`,
        actions: [{label: 'In Progress Applications', to: {name: 'reports', params: {view: 'applications'}}, icon: 'fa-tasks'}],
    },
    {
        id: 'application-fee',
        topic: 'completing',
        q: 'I already paid for the course. Why is it asking me to pay again?',
        keywords: 'pay again already paid charged again second payment application fee clock hour fee certificate fee why fee extra fee additional fee',
        a: `<p>Your course or registration fee covers the training itself. Some <strong>certifications and forms</strong> you can apply for at the end carry their own fee, separate from the course. Most often this is the fee for <strong>Washington State clock hours</strong>.</p>
<ul>
<li>You see the fee before you apply, and it's only charged <strong>after your application is approved</strong>, when the award is issued.</li>
<li>Many of these applications are <strong>optional</strong>. If you don't need that certification, you don't need to apply or pay (see "Do I need every application on my course?").</li>
<li>If you bought a bundle, the clock-hour fees for your bundle courses are billed this way as you complete each course.</li>
</ul>
<p>Application fees are non-refundable, so apply only for what you need.</p>`,
        actions: [{label: 'In Progress Applications', to: {name: 'reports', params: {view: 'applications'}}, icon: 'fa-tasks'}],
    },
    {
        id: 'optional-applications',
        topic: 'completing',
        q: 'Do I need every application on my course?',
        keywords: 'optional application required application which certificate two certificates both certificates do i need clock hours coach foundations of coaching finalforms',
        a: `<p>No. Applications marked <strong>Required</strong> must be completed to finish the course. The rest are <strong>optional</strong>, and you can choose one, several, or none. Each one you apply for is a separate certification, with its own fee if it has one.</p>
<p>Before you apply, check which certification you actually need:</p>
<ul>
<li><strong>Washington State clock hours</strong> are for certificated educators earning professional development hours for renewal or salary placement. If that's not you, you don't need them.</li>
<li><strong>Coaches</strong> don't need clock hours. They don't sync to FinalForms, and there's no fee to sync a WIAA certification. Foundations of Coaching courses are optional and don't award a WIAA certification.</li>
</ul>
<p>Not sure which one you need? Ask before you apply. Application fees are non-refundable.</p>`,
        contact: true,
    },
    {
        id: 'application-fee-refund',
        topic: 'completing',
        q: 'Are application fees refundable?',
        keywords: 'application fee refund clock hour fee refund certificate fee refund wrong certificate applied by mistake',
        a: `<p>No. Application fees are non-refundable. The fee is only charged once your application is approved and the award is issued, so by the time you pay, the certificate or form has been created for you.</p>
<p>Before paying, make sure it's a certification you need: many applications are optional (see "Do I need every application on my course?"). If you're unsure, send us a message first.</p>`,
        contact: true,
    },
    {
        id: 'application-fee-unpaid',
        topic: 'completing',
        q: "I paid the application fee, but it still says payment is needed",
        keywords: 'paid fee still says unpaid payment needed already paid fee receipt shows paid application wont complete',
        a: `<p>First, check <strong>Certificates &amp; Forms › Receipts</strong> to see which application your payment was for. Courses can offer more than one optional certificate, and each has its own fee, so the payment may belong to a different one.</p>
<p>If a payment for this application isn't listed there (for example, the checkout didn't finish even though you received a receipt), send us a message with your receipt number and we'll look into it. Please don't pay again until we've checked.</p>`,
        actions: [{label: 'Receipts', to: {name: 'reports', params: {view: 'receipts'}}, icon: 'fa-file-invoice'}],
        contact: true,
    },
    {
        id: 'use-all-hours',
        topic: 'completing',
        q: 'Do I have to use all the hours I enrolled in?',
        keywords: 'use all hours fewer hours finish early unused hours partial',
        a: `<p>No. Once you've met the course's minimum, you can finish and apply for the hours you've logged. Hours you don't use are forfeited when the course is completed.</p>
<p>To keep unused hours, move them to another course first with <strong>Reallocate Hours</strong> (see "Can I move hours to a different course?").</p>`,
    },
    {
        id: 'add-time-after',
        topic: 'completing',
        q: 'Can I add time after my course is completed?',
        keywords: 'completed too early add more time reopen course uncomplete',
        a: `<p>No. Completing a course locks its study log and can't be undone, so log all of your time and apply for everything you need before you finish.</p>
<p>Unused hours can be moved to another course before you complete (see "Can I move hours to a different course?").</p>`,
    },

    // ── Clock Hour Forms & OSPI ────────────────────────────────────────
    {
        id: 'find-forms',
        topic: 'clockhours',
        certification: 1,
        q: 'Where do I find my clock-hour form (FORM SPI 1125)?',
        keywords: 'spi 1125 clock hour form certificate download print where forms transcript',
        a: `<p>After your application is approved (and any fee is paid), download the form from <strong>Certificates &amp; Forms › Completed Certificates</strong>. Each award has its own download button, organized by year. The same files are on the last step of the application inside the course.</p>
<p>Each SPI 1125 is the official record for that course. The <strong>Printable Study Log</strong> is your activity record, not a clock-hour form.</p>`,
        actions: [{label: 'Completed Certificates', to: {name: 'reports', params: {view: 'completed'}}, icon: 'fa-file-certificate'}],
    },
    {
        id: 'submit-ospi',
        topic: 'clockhours',
        certification: 1,
        q: 'Do I send my clock hours to OSPI or my district?',
        keywords: 'submit ospi e-certification eds district hr salary placement renewal send hours',
        a: `<p>You do. We don't submit forms on your behalf.</p>
<ul>
<li><strong>Certificate renewal:</strong> enter the information from your SPI 1125 in your OSPI E-Certification (EDS) account.</li>
<li><strong>Salary schedule placement:</strong> give your forms to your district's HR or payroll office, following their process (your union rep can also help).</li>
</ul>
<p>If your district requires a signed paper copy, print the form and sign it.</p>`,
    },
    {
        id: 'approved-provider',
        topic: 'clockhours',
        certification: 1,
        q: "My district can't find you on the approved provider list",
        keywords: 'approved provider agency sponsor district cant find wfis school code institution code pesb',
        a: `<p>The approved clock-hour agency (sponsor) for each course is printed on its SPI 1125. Check the form itself rather than assuming. Sponsors include organizations such as the Washington Federation of Independent Schools (WFIS) and the 247 Educational Scholarship Association.</p>
<p>If the agency on your form is WFIS and you're asked for a school code, it's <strong>0000001749</strong>.</p>`,
    },
    {
        id: 'course-number',
        topic: 'clockhours',
        certification: 1,
        q: 'Where is the course number? My course name is cut off, or two forms have the same name',
        keywords: 'course number course id reference number same name duplicate form hr two forms cut off truncated title too long bottom of form',
        a: `<p>The course name box on the FORM SPI 1125 is small, so longer course titles can be cut off there. For convenience, we repeat the <strong>full course title at the bottom of the form</strong>, followed by a reference number in parentheses and the hours on that form, for example:</p>
<p><em>Educational Book Review: Your Title (C208748) | Clock Hours: 25 | STEM Hours: N/A</em></p>
<p>The reference number (the "C" number) is unique to each enrollment. Two forms for the same course always have different numbers, so use it to tell them apart or to point your district's HR to the right form.</p>
<p>If a course title itself needs to be corrected, send us a message.</p>`,
        contact: true,
    },
    {
        id: 'cert-number-wrong',
        topic: 'clockhours',
        certification: 1,
        q: 'My OSPI certificate number is missing or wrong',
        keywords: 'ospi number certificate number esa wrong missing cert number form',
        a: `<p>Update it under <strong>Your Account › Certifications &amp; Organizations</strong> on the clock-hours card and press <strong>Save Changes</strong>. If the number is locked, or a form you've already downloaded needs correcting, send us a message.</p>`,
        actions: [{label: 'Certifications & Organizations', to: {name: 'account', params: {section: 'certs'}}, icon: 'fa-id-card'}],
        contact: true,
    },
    {
        id: 'out-of-state',
        topic: 'clockhours',
        certification: 1,
        q: 'Do my clock hours count in another state?',
        keywords: 'another state out of state ceu transfer other states count',
        a: `<p>Our courses award Washington State approved clock hours. Whether they count somewhere else is up to that state's certification office, so check with them.</p>`,
    },
    {
        id: 'pd-section',
        topic: 'clockhours',
        certification: 1,
        q: 'What is the Professional Development section?',
        keywords: 'professional development renewal progress salary schedule column pd section',
        a: `<p><strong>Professional Development</strong> (in the top bar) brings your clock-hour picture together. It shows your <strong>Renewal Progress</strong> toward your certificate renewal date, your district's <strong>Salary Schedule</strong> and what your next column needs, and your clock-hour history by year. Add your district, column and renewal date under its <strong>Settings</strong>.</p>
<p>It appears when Washington clock hours is turned on under <strong>Your Account › Certifications &amp; Organizations</strong>. Your district's salary office is the final word on how many hours you need.</p>`,
        actions: [{label: 'Professional Development', to: {name: 'pd'}, icon: 'fa-arrow-trend-up'}],
    },

    // ── Expiration, Reactivation & Changes ─────────────────────────────
    {
        id: 'expiration',
        topic: 'expiration',
        q: 'Do courses expire?',
        keywords: 'expire expiration expiry how long access expires date',
        a: `<p>Most do. When you enroll, the page tells you how long the enrollment is valid and the date you have access through. Your course card shows <strong>Expires in N days</strong> as the date gets close.</p>
<p>After it expires, the course is locked until you reactivate it. Completed courses never expire.</p>`,
        actions: [{label: 'Current Enrollments', to: {path: '/enrollments'}, icon: 'fa-circle-play'}],
    },
    {
        id: 'reactivate',
        topic: 'expiration',
        q: 'My course expired. Can I reactivate it?',
        keywords: 'reactivate expired course reactivation fee reopen expired',
        a: `<p>Yes. Use <strong>Reactivate</strong> on the course card or the <strong>Reactivate My Courses</strong> prompt on your Dashboard. You can choose:</p>
<ul>
<li><strong>Completed Hours Only</strong>: free. It reopens the course so you can finish and apply for the hours you've already logged.</li>
<li><strong>Full Course</strong> or <strong>Partial Hours</strong>: a per-hour fee for hours you haven't logged yet.</li>
</ul>
<p>Reactivation extends the course for a year. Reactivation fees are non-refundable.</p>
<p>If the course expired more than a year ago, reactivating creates a <strong>new enrollment</strong>, and progress, logs, notes and reflections don't carry over.</p>`,
        actions: [{label: 'Current Enrollments', to: {path: '/enrollments'}, icon: 'fa-circle-play'}],
    },
    {
        id: 'grace-period',
        topic: 'expiration',
        q: 'Is there a grace period after my course expires?',
        keywords: 'grace period just expired extension extend few days',
        a: `<p>Yes. If a course expired within the last <strong>5 days</strong> and hasn't been reactivated before, you can reactivate it at no charge for 5 more days to log remaining work and submit reflections. It isn't meant for starting new work.</p>`,
    },
    {
        id: 'extension',
        topic: 'expiration',
        q: 'Can I get an extension before my course expires?',
        keywords: 'extension extend before expires more time',
        a: `<p>Reactivation options open once a course expires. Until then, keep an eye on the date on your course card. If you have special circumstances, send us a message and our team will take a look.</p>`,
        contact: true,
    },
    {
        id: 'reallocate',
        topic: 'expiration',
        q: 'Can I move hours to a different course, or split a course?',
        keywords: 'reallocate move hours different course split switch change course swap transfer hours',
        a: `<p>Yes, for self-directed courses. Open the course, go to <strong>Your Enrollment</strong> and press <strong>Reallocate Hours</strong>. Hours you haven't used can be moved into one or more new courses at no cost. Hours you've already logged stay where they are.</p>
<ul>
<li>Each new course needs at least 1 hour, and the same course can receive hours up to 3 times.</li>
<li>Set up and get approval for the new course's study plan before logging time in it.</li>
<li>Reallocation can't be undone.</li>
</ul>
<p>It isn't available for completed or expired courses, or while an application is in progress. The screen tells you why if it's blocked.</p>`,
    },
    {
        id: 'remove-enrollment',
        topic: 'expiration',
        q: 'Can I remove a course I enrolled in by mistake?',
        keywords: 'remove course delete enrollment enrolled by mistake wrong course cancel enrollment',
        a: `<p>Send us a message with the course name and our team will take care of it. If you paid for it, see "Can I get a refund?". A course you just don't need any more can simply be left to expire.</p>`,
        contact: true,
    },

    // ── Conferences ────────────────────────────────────────────────────
    {
        id: 'register-conference',
        topic: 'conferences',
        q: 'How do I register for a conference?',
        keywords: 'register conference registration conference center sign up event badge code',
        a: `<p>Open <strong>Conferences › Conference Center</strong>, choose the conference and press <strong>Register Now</strong>. Pick a <strong>Registration Type</strong>, enter any <strong>Access/Discount Code</strong> you were given (such as a code from your badge), then pay and submit.</p>
<p>Your registration then appears under <strong>Conferences › Conference Records</strong>. Records are grouped by year.</p>`,
        actions: [
            {label: 'Conference Center', to: {name: 'conference-center'}, icon: 'fa-presentation'},
            {label: 'Conference Records', to: {name: 'registrations'}, icon: 'fa-screen-users'},
        ],
    },
    {
        id: 'conference-credit',
        topic: 'conferences',
        q: 'How do I get credit for a conference I attended?',
        keywords: 'conference credit clock hours attendance confirm attendance sessions apply conference',
        a: `<ol>
<li>Open the conference from <strong>Conference Records</strong>.</li>
<li>Add the sessions you attended under <strong>Sessions &amp; Schedule</strong>, or watch sessions under <strong>On-Demand Content</strong>.</li>
<li>In <strong>Record &amp; Attendance Log</strong>, press <strong>Confirm Attendance</strong> for each session after it ends.</li>
<li>Apply under <strong>Certifications &amp; Forms</strong> inside the conference.</li>
</ol>
<p>Some conferences require a minimum number of minutes, sessions or specific tracks. The application lists what's needed.</p>`,
        actions: [{label: 'Conference Records', to: {name: 'registrations'}, icon: 'fa-screen-users'}],
    },
    {
        id: 'conference-partner-approval',
        topic: 'conferences',
        q: 'My conference application has been waiting for approval a long time',
        keywords: 'requires approval waiting weeks partner wsca counselor association approve conference application',
        a: `<p>For some partner conferences, the hosting organization reviews attendance and approves the award, and we don't have their attendance records. If it's been waiting a while, contact the organization that ran the conference (you can use <strong>Contact Organizer</strong> in the registration).</p>`,
    },
    {
        id: 'conference-po',
        topic: 'conferences',
        q: 'How do I pay for a conference with a purchase order?',
        keywords: 'purchase order po invoice district pay conference po request',
        a: `<p>When the organizer allows it, choose <strong>Pay with Purchase Order (P.O.)</strong> at registration. Then open the registration's <strong>Purchase Order (P.O.)</strong> page and press <strong>Save &amp; Generate</strong> to create the P.O. request (invoice) for your district.</p>
<p>Your registration isn't complete until the organizer receives the P.O. Until then, some features stay locked. Questions about a P.O. go to the organizer through <strong>Contact Organizer</strong>.</p>`,
    },
    {
        id: 'conference-change',
        topic: 'conferences',
        q: 'I need to change my clinic date or location, or I can\'t attend',
        keywords: 'change date location cant attend transfer clinic switch cancel registration',
        a: `<p>Each conference organizer manages its own sessions, transfers and cancellations. For WIAA clinics that's WIAA. Use <strong>Contact Organizer</strong> in your registration. If you registered for the wrong thing, send us a message too.</p>`,
        contact: true,
    },
    {
        id: 'registration-closed',
        topic: 'conferences',
        q: 'It says "Registration Closed"',
        keywords: 'registration closed cant register old conference past conference',
        a: `<p>Each conference has its own registration window set by the organizer. Outside that window, registration is closed. For older conferences that are still open, the current registration options (and any fee) are the only ones available.</p>`,
    },

    // ── Payments, Receipts & Refunds ───────────────────────────────────
    {
        id: 'receipts',
        topic: 'payments',
        q: 'Where can I find a receipt for reimbursement?',
        keywords: 'receipt reimbursement district employer proof of payment paid invoice itemized',
        a: `<p>Every card payment is listed under <strong>Certificates &amp; Forms › Receipts</strong>: courses, bundles, reactivations, conferences and application fees. Use <strong>View Receipt</strong> on any of them. A receipt was also e-mailed to you when you paid, so check your spam folder if you can't find it.</p>
<p>Need an itemized receipt or invoice that isn't listed, or a receipt from before our platform update? Send us a message.</p>`,
        actions: [{label: 'Receipts', to: {name: 'reports', params: {view: 'receipts'}}, icon: 'fa-file-invoice'}],
        contact: true,
    },
    {
        id: 'payment-plans',
        topic: 'payments',
        q: 'I have a payment plan. Where do I manage it?',
        keywords: 'payment plan the payment option tpo monthly payment financing loan agreement bank sales tax plan balance',
        a: `<p>Payment plans are serviced by <strong>The Payment Option (TPO)</strong>, a separate company. Log in to your TPO account to manage your plan, and contact TPO directly for help with anything on their platform.</p>`,
        actions: [{label: 'Log In to TPO', href: TPO_URL, external: true, icon: 'fa-up-right-from-square'}],
    },
    {
        id: 'refunds',
        topic: 'payments',
        q: 'Can I get a refund?',
        keywords: 'refund cancel money back charged return non refundable payment plan bundle representative',
        a: `<p>It depends on how the course was purchased. The terms you agreed to at checkout apply:</p>
<ul>
<li><strong>Payment plan, bundle or representative purchases:</strong> non-refundable, whether or not the courses have been started or completed. Courses you don't use simply expire. For anything about the payment plan itself, log in to your account with The Payment Option (TPO) or contact TPO directly.</li>
<li><strong>A single course paid by card:</strong> refundable until you start the course. After that it's non-refundable, but it may be credited toward another course or subject.</li>
<li><strong>Reactivation fees and application fees</strong> (such as clock-hour fees): non-refundable.</li>
<li><strong>Conferences and clinics:</strong> refunds and transfers are up to the organizer (WIAA for its clinics). Use <strong>Contact Organizer</strong> in your registration.</li>
</ul>
<p>Want unused hours in a different course instead? See "Can I move hours to a different course?". To ask about a refund or credit on a card purchase, send us a message with what you bought and why.</p>`,
        actions: [{label: 'Log In to TPO', href: TPO_URL, external: true, icon: 'fa-up-right-from-square'}],
        contact: true,
    },
    {
        id: 'charged-not-registered',
        topic: 'payments',
        q: "I was charged but my course or registration isn't showing",
        keywords: 'charged not registered paid twice double charged payment failed card not showing',
        a: `<p>Check <strong>Courses › Current Enrollments</strong>, <strong>Conferences › Conference Records</strong> and <strong>Certificates &amp; Forms › Receipts</strong> first. If you were charged and it's not there (or you were charged twice), send us a message with the date and amount and we'll sort it out.</p>
<p>If the card form won't appear at checkout, try another browser or a private window.</p>`,
        actions: [{label: 'Receipts', to: {name: 'reports', params: {view: 'receipts'}}, icon: 'fa-file-invoice'}],
        contact: true,
    },
    {
        id: 'sales-tax',
        topic: 'payments',
        q: 'Why was sales tax added?',
        keywords: 'sales tax tax washington digital services charged tax',
        a: `<p>Washington applies sales tax to digital services, including online professional development. Tax is calculated from your address at checkout and shown before you pay. Keep your address current under <strong>Your Account › Account Settings</strong>.</p>
<p>If you have a payment plan, questions about sales tax on the plan go to The Payment Option (see "I have a payment plan").</p>`,
    },
    {
        id: 'district-po-courses',
        topic: 'payments',
        q: 'Can my district pay for my courses?',
        keywords: 'district pay school pay purchase order courses invoice employer pay',
        a: `<p>For conferences, use the purchase order option when the organizer offers it. For online courses paid by a district purchase order, send us a message listing the courses you need and our team will help.</p>`,
        contact: true,
    },
    {
        id: 'tax-form',
        topic: 'payments',
        q: 'Do you provide a tax form (like a 1098)?',
        keywords: '1098 tax form tuition tax deduction',
        a: `<p>No, we don't issue tax forms. Your receipts under <strong>Certificates &amp; Forms › Receipts</strong> show what you paid.</p>`,
    },

    // ── E-Mails & Text Messages ────────────────────────────────────────
    {
        id: 'notification-settings',
        topic: 'notifications',
        q: 'How do I choose which e-mails and texts I get?',
        keywords: 'notifications email preferences turn off emails too many emails settings',
        a: `<p>Go to <strong>Your Account › Notifications</strong>. Each topic (study plans and reflections, applications, conference updates, monthly check-ins, news) can be turned on or off for e-mail and, where available, for text.</p>
<p>Account messages (login codes, password resets and enrollment confirmations) are always sent so you never miss one.</p>`,
        actions: [{label: 'Notifications', to: {name: 'account', params: {section: 'notifications'}}, icon: 'fa-bell'}],
    },
    {
        id: 'texts',
        topic: 'notifications',
        q: 'How do I turn text messages on or off?',
        keywords: 'text messages sms stop opt in opt out turn on texts',
        a: `<p><strong>To turn them on:</strong> go to <strong>Your Account › Notifications</strong>, check the consent box and press <strong>Turn On Text Messages</strong>. You need a mobile number in Account Settings.</p>
<p><strong>To turn them off:</strong> reply <strong>STOP</strong> to any text from us, or switch them off on the same screen. Reply <strong>START</strong> to undo a STOP.</p>`,
        actions: [{label: 'Notifications', to: {name: 'account', params: {section: 'notifications'}}, icon: 'fa-bell'}],
    },
    {
        id: 'unsubscribe',
        topic: 'notifications',
        q: 'How do I unsubscribe from e-mails?',
        keywords: 'unsubscribe stop emails opt out mailing',
        a: `<p>Use the <strong>Unsubscribe</strong> link at the bottom of any optional e-mail to stop that kind of message, or manage every topic under <strong>Your Account › Notifications</strong>.</p>`,
        actions: [{label: 'Notifications', to: {name: 'account', params: {section: 'notifications'}}, icon: 'fa-bell'}],
    },
    {
        id: 'not-getting-emails',
        topic: 'notifications',
        q: "I'm not getting your e-mails",
        keywords: 'not getting emails no email spam junk didnt receive code email',
        a: `<p>Check your spam or junk folder, and add ${EMAIL} to your contacts. School and district e-mail systems sometimes filter outside mail, so a personal address can be more reliable. You can change or add addresses under <strong>Your Account › Login Credentials</strong>.</p>
<p>For login codes, you can also choose <strong>Login with your Phone</strong> to get a text instead.</p>`,
        actions: [{label: 'Login Credentials', to: {name: 'account', params: {section: 'login'}}, icon: 'fa-key'}],
    },

    // ── Getting Help ───────────────────────────────────────────────────
    {
        id: 'contact-us',
        topic: 'support',
        q: 'How do I contact your team?',
        keywords: 'contact support help email phone talk to someone message',
        a: `<p>The fastest way is to <strong>send us a message</strong> from this page. Your conversation and our replies stay under <strong>Your Tickets</strong>, so check back there. You can also e-mail ${mail} or text ${LEGAL.smsNumber}.</p>
<p>To help us answer in one reply, tell us which course or conference you're asking about and what you see on screen.</p>`,
        contact: true,
    },
    {
        id: 'what-support-does',
        topic: 'support',
        q: 'What can only your team do for me?',
        keywords: 'support only merge name change refund send back reflection remove enrollment disable account',
        a: `<p>Send us a message for any of these:</p>
<ul>
<li>Merging duplicate accounts, or finding an account you can't get into.</li>
<li>Name changes (with a photo of your updated ID).</li>
<li>Refunds, credits, and charges that didn't register.</li>
<li>Sending back a reflection or application you submitted by mistake.</li>
<li>Removing an enrollment.</li>
<li>Correcting a completed form, or changing a locked certification number.</li>
<li>Itemized receipts and invoices.</li>
<li>Disabling your account (it's kept for record keeping).</li>
</ul>
<p>For questions about other organizations' systems, contact them directly: FinalForms (${FINALFORMS_EMAIL}), WIAA, OSPI, or The Payment Option for payment plans.</p>`,
        contact: true,
    },
];

const TOPIC_KEYS = new Set(HELP_TOPICS.map(t => t.key));
HELP_ARTICLES.forEach(a => {
    if (!TOPIC_KEYS.has(a.topic)) throw new Error(`helpCenter: article ${a.id} has unknown topic ${a.topic}`);
});

export function helpTopic(key) {
    return HELP_TOPICS.find(t => t.key === key) || null;
}

export function helpArticle(id) {
    return HELP_ARTICLES.find(a => a.id === id) || null;
}

export function topicArticles(key) {
    return HELP_ARTICLES.filter(a => a.topic === key);
}
